import React, { useEffect, useMemo, useState } from "react";
import { walkthroughAPI } from "../services/api";
import {
  getDismissedObservationIds,
  undismissObservation,
} from "../utils/observationNotifications";
import "./ObservationHistoryPage.css";

const CHECKLIST_LABELS = {
  objective_posted: "Objective posted and visible",
  aligned_to_teks: "Aligned to TEKS standard",
  higher_order_questioning: "Higher-order questioning observed",
  student_engagement: "Student engagement visible",
};

const toDateLabel = (dateValue) => {
  const raw = String(dateValue || "").trim();
  if (!raw) return "Unknown date";
  const dateOnly = raw.includes("T") ? raw.split("T")[0] : raw;
  const parsed = new Date(`${dateOnly}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return dateOnly;
  return parsed.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const hasChecklistSelections = (checklist) =>
  checklist && typeof checklist === "object" && Object.values(checklist).some(Boolean);

const hasSharedFeedback = (observation = {}) =>
  Boolean(String(observation.notes || "").trim()) || hasChecklistSelections(observation.checklist);

export default function ObservationHistoryPage({ authProfile }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [dismissedIds, setDismissedIds] = useState(new Set());

  const userId = authProfile?.userId || authProfile?.id || "";
  const isTeacher = authProfile?.role === "teacher";

  useEffect(() => {
    if (!isTeacher || !userId) {
      setDismissedIds(new Set());
      return;
    }
    setDismissedIds(getDismissedObservationIds(userId));
  }, [isTeacher, userId]);

  useEffect(() => {
    if (!isTeacher) {
      setRows([]);
      setError("");
      setLoading(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await walkthroughAPI.getMySharedObservations();
        if (cancelled) return;
        const payload = Array.isArray(response?.data) ? response.data : [];
        setRows(payload.filter(hasSharedFeedback));
      } catch (loadError) {
        if (cancelled) return;
        const apiError = String(loadError?.response?.data?.error || "").trim();
        setError(apiError || "Could not load shared observation notes.");
        setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [isTeacher]);

  const orderedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const rawATime = new Date(a.updatedAt || a.createdAt || a.observationDate || 0).getTime();
      const rawBTime = new Date(b.updatedAt || b.createdAt || b.observationDate || 0).getTime();
      const aTime = Number.isNaN(rawATime) ? 0 : rawATime;
      const bTime = Number.isNaN(rawBTime) ? 0 : rawBTime;
      return bTime - aTime;
    });
    return copy;
  }, [rows]);

  if (!isTeacher) {
    return (
      <div className="panel-card">
        <p className="admin-error">Observation history is available for teacher accounts.</p>
      </div>
    );
  }

  return (
    <section className="observation-history-page panel-card">
      <div className="observation-history-head">
        <h2>Principal Observation Notes</h2>
        <p>Shared feedback history, newest first.</p>
      </div>

      {loading ? <p className="muted">Loading shared notes…</p> : null}
      {!loading && error ? <p className="observation-history-error">{error}</p> : null}
      {!loading && !error && orderedRows.length === 0 ? (
        <p className="muted">No shared observation notes yet.</p>
      ) : null}

      {!loading && !error && orderedRows.length > 0 ? (
        <div className="observation-history-list">
          {orderedRows.map((row) => {
            const dismissed = dismissedIds.has(String(row.id));
            const checklistEntries = Object.entries(row.checklist || {}).filter(([, checked]) => Boolean(checked));

            return (
              <article key={row.id} className="observation-history-card">
                <div className="observation-history-card-head">
                  <div>
                    <strong>{toDateLabel(row.observationDate)}</strong>
                    <p>
                      {row.principalName ? `Shared by ${row.principalName}` : "Shared by principal"}
                    </p>
                  </div>
                  <div className="observation-history-chip-row">
                    <span className={`observation-history-chip ${dismissed ? "is-muted" : "is-new"}`}>
                      {dismissed ? "Dismissed from bell" : "New"}
                    </span>
                    {dismissed ? (
                      <button
                        type="button"
                        className="nav-lite-btn"
                        onClick={() => setDismissedIds(undismissObservation(userId, row.id))}
                      >
                        Mark as New
                      </button>
                    ) : null}
                  </div>
                </div>

                {String(row.notes || "").trim() ? (
                  <p className="observation-history-notes">{row.notes}</p>
                ) : (
                  <p className="muted">No note text provided.</p>
                )}

                {checklistEntries.length > 0 ? (
                  <ul className="observation-history-checklist">
                    {checklistEntries.map(([key]) => (
                      <li key={`${row.id}-${key}`}>{CHECKLIST_LABELS[key] || key}</li>
                    ))}
                  </ul>
                ) : null}
              </article>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}
